import classNames from 'classnames';
import { PropsWithChildren, ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import styled, { CSSProperties } from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  FOOTBALL_PLAY_PRO_ONBOARDING_CHOOSE_YOUR_LEVEL,
  FOOTBALL_PLAY_PRO_ONBOARDING_SELECT_COMPETITION,
} from '@sorare/core/src/constants/__generated__/routes';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import {
  desktopAndAbove,
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { MarketGuidanceLayoutHeader } from './MarketGuidanceLayoutHeader';
import { marketGuidanceBackgroundStyles } from './ui';

const Root = styled(Vertical)`
  ${marketGuidanceBackgroundStyles}
  overflow-x: clip;
  min-height: var(--100vh);
  padding: var(--double-unit);
`;
const PageContent = styled(Vertical).attrs({ gap: 2.5 })`
  width: 100%;
  @media ${tabletAndAbove} {
    margin: 0 auto auto auto;
    width: 70%;
  }
  @media ${laptopAndAbove} {
    margin: 0 auto auto auto;
    width: 60%;
  }
  @media ${desktopAndAbove} {
    margin: 0 auto auto auto;
    width: 50%;
  }
  &.fullHeight {
    flex: 1;
  }
`;
const ChildrenWrapper = styled(Vertical)`
  width: 100%;
  &.fullHeight {
    flex: 1;
  }
`;
const CtaWrapper = styled(Vertical).attrs({ center: true })`
  margin-top: auto;
  position: sticky;
  bottom: var(--double-unit);
`;

type Props = {
  center?: boolean;
  primaryBackgroundColor?: string;
  secondaryBackgroundColor?: string;
  cta?: ReactNode;
  progress: number;
  onBack?: () => void;
  noBackButton?: boolean;
};
export const MarketGuidanceLayout = ({
  center,
  primaryBackgroundColor,
  secondaryBackgroundColor,
  cta,
  children,
  progress,
  onBack,
  noBackButton,
}: PropsWithChildren<Props>) => {
  const {
    flags: { useNewMarketGuidanceOnboarding = false },
  } = useFeatureFlags();
  const { isMobileApp } = useIsMobileApp();
  const navigate = useNavigate();

  if (isMobileApp) {
    return (
      <Navigate to={FOOTBALL_PLAY_PRO_ONBOARDING_SELECT_COMPETITION} replace />
    );
  }

  if (!useNewMarketGuidanceOnboarding) {
    return <Navigate to={FOOTBALL_PLAY_PRO_ONBOARDING_CHOOSE_YOUR_LEVEL} />;
  }

  return (
    <Root
      style={
        {
          '--primary-background-color': primaryBackgroundColor,
          '--secondary-background-color': secondaryBackgroundColor,
        } as CSSProperties
      }
    >
      <PageContent className={classNames({ fullHeight: center })}>
        <MarketGuidanceLayoutHeader
          noBackButton={noBackButton}
          onBack={onBack || (() => navigate(-1))}
          progress={progress}
        />
        <ChildrenWrapper
          center={center}
          className={classNames({ fullHeight: center })}
        >
          {children}
        </ChildrenWrapper>
      </PageContent>
      <CtaWrapper>{cta}</CtaWrapper>
    </Root>
  );
};
